import { useCallback, useMemo, useState } from 'react'
import * as Sentry from '@sentry/nextjs'

import { useAssignTags } from '@/feature/tags/api'
import { AssetTag } from '@/feature/tags/components'
import { useTagsStore } from '@/feature/tags/store'
import { Asset } from '@/lib/features/Assets/interfaces'

import { PendingTagChange } from './BulkTagsModal.types'
import {
  computeAssetTagUpdates,
  computeTagLimitError,
  computeTagStates,
  computeToggleChange,
  getAssetLabel,
  mergePendingIntoStates,
} from './BulkTagsModal.utils'

export const useBulkTagsModal = (selectedAssets: Asset[], onApplySuccess?: () => void) => {
  const { globalOrgTags, isTagsUpdating, setTagsUpdating } = useTagsStore()
  const { trigger: assignTags } = useAssignTags()

  const [searchTerm, setSearchTerm] = useState('')
  const [pendingChanges, setPendingChanges] = useState<Map<number, PendingTagChange>>(new Map())
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [overrideConfirm, setOverrideConfirm] = useState<{
    tagId: number
    position: { x: number; y: number }
  } | null>(null)

  const baseTagStates = useMemo(
    () => computeTagStates(selectedAssets, globalOrgTags),
    [selectedAssets, globalOrgTags],
  )

  const effectiveTagStates = useMemo(
    () => mergePendingIntoStates(baseTagStates, pendingChanges),
    [baseTagStates, pendingChanges],
  )

  const applyError = useMemo(
    () => computeTagLimitError(selectedAssets, pendingChanges),
    [selectedAssets, pendingChanges],
  )

  const filteredTags = useMemo(() => {
    const term = searchTerm.toLowerCase()
    return term ? globalOrgTags.filter((t) => t.name.toLowerCase().includes(term)) : globalOrgTags
  }, [globalOrgTags, searchTerm])

  const handleTagClick = useCallback(
    (tag: AssetTag, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const current = effectiveTagStates.get(tag.id) ?? 'none'

      if (current === 'some') {
        const rect = event.currentTarget.getBoundingClientRect()
        setOverrideConfirm({ tagId: tag.id, position: { x: rect.left, y: rect.bottom + 4 } })
        return
      }

      const base = baseTagStates.get(tag.id) ?? 'none'
      setPendingChanges((prev) => computeToggleChange(prev, tag.id, current, base))
    },
    [effectiveTagStates, baseTagStates],
  )

  const handleConfirmOverride = useCallback(
    (action: 'add' | 'remove') => {
      if (overrideConfirm === null) return
      setPendingChanges((prev) => new Map(prev).set(overrideConfirm.tagId, action))
      setOverrideConfirm(null)
    },
    [overrideConfirm],
  )

  const handleDismissOverride = useCallback(() => setOverrideConfirm(null), [])

  const handleClose = useCallback(() => {
    setPendingChanges(new Map())
    setSearchTerm('')
    setOverrideConfirm(null)
    setNetworkError(null)
  }, [])

  const handleApply = useCallback(async () => {
    if (pendingChanges.size === 0 || applyError !== null) return

    const updates = computeAssetTagUpdates(selectedAssets, pendingChanges)

    setNetworkError(null)
    setTagsUpdating(true)

    const failedBridgeIds: string[] = []

    try {
      for (const { assetId, tagIds } of updates) {
        try {
          await assignTags({ params: { asset_id: assetId }, data: { tag_ids: tagIds } })
        } catch (error) {
          Sentry.captureException(error, {
            extra: { assetId },
            tags: { feature: 'bulk-tags' },
          })
          failedBridgeIds.push(getAssetLabel(selectedAssets, assetId))
        }
      }
    } finally {
      setTagsUpdating(false)
    }

    if (failedBridgeIds.length > 0) {
      setNetworkError(
        `Failed to update ${failedBridgeIds.length === 1 ? 'asset' : 'assets'}: ${failedBridgeIds.join(', ')}. Please try again.`,
      )
    }

    if (failedBridgeIds.length === 0) {
      handleClose()
      onApplySuccess?.()
    }
  }, [pendingChanges, applyError, selectedAssets, assignTags, onApplySuccess, handleClose])

  const overrideConfirmTag = overrideConfirm
    ? (globalOrgTags.find((t) => t.id === overrideConfirm.tagId) ?? null)
    : null

  const overridePosition = overrideConfirm?.position ?? null

  return {
    searchTerm,
    setSearchTerm,
    filteredTags,
    effectiveTagStates,
    pendingChanges,
    isApplying: isTagsUpdating,
    applyError,
    networkError,
    overrideConfirmTag,
    overridePosition,
    handleTagClick,
    handleConfirmOverride,
    handleDismissOverride,
    handleApply,
    handleClose,
  }
}
